// @flow
import * as React from "react";
import { observer } from "mobx-react";
import classNames from "classnames";
import ContainerSection from "../../ratecards/components/ContainerSection";
import LoadingIndicator from "../../shared/LoadingIndicator";
import CheckBox from "../../shared/CheckBox";
import TextInput from "../../../components/lib/TextInput";
import {
  Card,
  CardHeader,
  CardHeaderTitle,
  CardListSubText,
  SubTextAccent,
} from "../../../components/lib/Card";

type Props = {
  store: Object,
  expanded: boolean,
  createstore: Object,
  ratecardId: any,
};
const PceRateCardDetail = (props: Props) => {
  const { store, expanded, createstore, ratecardId } = props;
  const network = store.network;
  const searchList = store.rateCardSearch;

  const className = classNames("search-peek", {
    "search-peek-expand": expanded,
    "search-peek-collapse": !expanded,
  });

  let tableContent = null;
  if (network.loading) {
    tableContent = <LoadingIndicator />;
  } else if (network.error) {
    tableContent = (
      <div className="pt-error-message">
        <h2>Error</h2>
        <pre>
          <code>{JSON.stringify(network.error, null, 2)}</code>
        </pre>
      </div>
    );
  } else if (searchList.length === 0) {
    tableContent = (
      <div className="ratecards-table-empty">
        {Object.keys(store.appliedFilters).length > 0 ? (
          <div>No Hourly Searches Found</div>
        ) : (
          <div>No Hourly Searches Found</div>
        )}
      </div>
    );
  } else {
    tableContent = searchList.map((sl) => (
      <RateCardSearches
        key={sl.searchId}
        search={sl}
        createstore={createstore}
        ratecardId={ratecardId}
        store={store}
      />
    ));
  }

  return (
    <div className={className}>
      <div className="search-peek-body">
        <ContainerSection className="overlap-b-padding no-border search-peek-levels flex-wrap">
          {tableContent}
        </ContainerSection>
      </div>
    </div>
  );
};

type RateCardSearchesProps = {
  search: Object,
  createstore: Object,
  ratecardId: any,
  store: Object,
};
const RateCardSearches = observer((props: RateCardSearchesProps) => {
  const search = props.search;
  const name = search.name;
  const state = search.state;
  const country = search.country;
  const buyrates = search.buyrate || [];
  const marketRows = search.marketrate || [];
  // A search may carry buyrates that have no punchouts; only real punchouts are selectable.
  const hasPunchouts = buyrates.some((buyrate) => (buyrate.punchout || []).length > 0);

  let buyratesList = null;
  if (hasPunchouts) {
    buyratesList = buyrates.map((buyrate) => (
      <RateCardSearchDetails
        key={buyrate.buyrateId}
        buyRate={buyrate}
        createstore={props.createstore}
        ratecardId={props.ratecardId}
        searchId={search.searchId}
        store={props.store}
      />
    ));
  } else if (marketRows.length > 0) {
    // PAY-15: no punchouts -> let the user pick a market bill rate instead.
    buyratesList = (
      <MarketRates rows={marketRows} store={props.store} currency={search.currency} />
    );
  } else {
    buyratesList = "No Buy Rate Found";
  }

  return (
    <Card className="view">
      <div className="rc-container bring-forward">
        <div className="info_head saved-search-details">
          <CardHeader>
            <CardHeaderTitle>{name}</CardHeaderTitle>
          </CardHeader>
          <CardListSubText>
            <SubTextAccent>{state}</SubTextAccent>
            <SubTextAccent> in</SubTextAccent>
            <SubTextAccent> {country}</SubTextAccent>
          </CardListSubText>
        </div>
        <div className="clearfix" />
        {buyratesList}
      </div>
    </Card>
  );
});

const RateCardSearchDetails = observer((props) => {
  const buyrate = props.buyRate;
  const name = buyrate.name;
  const id = buyrate.buyrateId;
  const punchouts = buyrate.punchout;

  let punchOutList = null;
  let punchoutTable = null;
  let rowListLengthArray = [1, 2, 3];
  let rowList = rowListLengthArray.map((row) => <Rows key={row} />);

  if (punchouts.length === 0) {
    punchoutTable = "No Available punchouts";
  } else {
    punchOutList = punchouts.map((buyRatepunchout) => (
      <Punchouts
        key={buyRatepunchout.levelId}
        punchout={buyRatepunchout}
        createstore={props.createstore}
        buyrateId={id}
        ratecardId={props.ratecardId}
        searchId={props.searchId}
        store={props.store}
      />
    ));
    punchoutTable = (
      <table className="no-spacing table table_label" cellSpacing="0">
        <thead className="">
          <tr>
            <th className="col-sm-1">Level</th>
            <th className="col-sm-3">Pay Rate</th>
            <th className="col-sm-3">Suggested Markup</th>
            <th className="col-sm-3">Suggested Bill Rate</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td />
            {rowList}
          </tr>
          {punchOutList}
        </tbody>
      </table>
    );
  }

  return (
    <div className="clearfix ratecard_new_table">
      <h4>{name}</h4>
      <br />
      {punchoutTable}
      <div className="clearfix" />
      <div className="clearfix" />
    </div>
  );
});

// Display punchout row with checkbox and the rest of the details
const Punchouts = observer((props) => {
  const pcstore = props.store;
  const punchout = props.punchout;
  const viewState = pcstore.punchoutViewState.get(props.punchout.punchoutId);
  const level = punchout.levelName;

  return (
    <tr>
      <td>
        <CheckBox
          type="checkbox"
          name="list-item-select"
          value={level}
          selected={viewState.selected}
          style={{
            width: 34,
          }}
          onClick={(e) => {
            pcstore.togglePunchoutSelect(punchout);
          }}
        />{" "}
        {level}
      </td>
      <td>
        <span className="col-sm-3">{punchout.payRateAvg}</span>
        <span className="col-sm-3">{punchout.payRateMin}</span>
        <span className="col-sm-3">{punchout.payRateMid}</span>
        <span className="col-sm-3">{punchout.payRateMax}</span>
      </td>
      <td>
        <span className="col-sm-3">{punchout.markupPctAvg}%</span>
        <span className="col-sm-3">{punchout.markupPctMin}%</span>
        <span className="col-sm-3">{punchout.markupPctMid}%</span>
        <span className="col-sm-3">{punchout.markupPctMax}%</span>
      </td>
      <td>
        <span className="col-sm-3">{punchout.billRateAvg}</span>
        <span className="col-sm-3">{punchout.billRateMin}</span>
        <span className="col-sm-3">{punchout.billRateMid}</span>
        <span className="col-sm-3">{punchout.billRateMax}</span>
      </td>
    </tr>
  );
});

const Rows = observer((props) => {
  return (
    <td>
      <span className="col-sm-3">Avg</span>
      <span className="col-sm-3">Min</span>
      <span className="col-sm-3">Mid</span>
      <span className="col-sm-3">Max</span>
    </td>
  );
});

// PAY-15: market-rate table shown for searches that have no punchouts. Each level
// row is selectable; the bill rate defaults to the market Max (NTE) or Min preset
// and is directly editable for a custom value. Amounts are in the search's local currency.
const MarketRates = observer((props) => {
  const store = props.store;
  const rows = props.rows;
  const iso = (props.currency && props.currency.iso) || "";

  return (
    <div className="clearfix ratecard_new_table">
      <h4>Market Bill Rates{iso ? ` (${iso})` : ""}</h4>
      <br />
      <table className="no-spacing table table_label" cellSpacing="0">
        <thead>
          <tr>
            <th className="col-sm-1">Level</th>
            <th className="col-sm-4">Market Bill Rate (Min / Max)</th>
            <th className="col-sm-3">Rate Type</th>
            <th className="col-sm-4">Bill Rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <MarketRateRow
              key={row.marketKey}
              row={row}
              store={store}
              currencyIso={iso}
            />
          ))}
        </tbody>
      </table>
      <div className="clearfix" />
    </div>
  );
});

// A single market-rate level row: pick Max (NTE)/Min, or type a custom bill rate.
const MarketRateRow = observer((props) => {
  const store = props.store;
  const row = props.row;
  const iso = props.currencyIso;
  const viewState = row.viewState;
  const money = (v) => (iso ? `${iso} ${v}` : `${v}`);

  return (
    <tr>
      <td>
        <CheckBox
          type="checkbox"
          name="list-item-select"
          value={row.levelName}
          selected={viewState.selected}
          style={{
            width: 34,
          }}
          onClick={() => {
            store.toggleMarketRowSelect(row);
          }}
        />{" "}
        {row.levelName}
      </td>
      <td>
        <span className="col-sm-6">Min {money(row.billRateMin)}</span>
        <span className="col-sm-6">Max {money(row.billRateMax)}</span>
      </td>
      <td>
        <select
          value={viewState.preset}
          onChange={(e) => store.setMarketRowPreset(row, e.target.value)}
        >
          <option value="MAX">Max (NTE)</option>
          <option value="MIN">Min</option>
        </select>
      </td>
      <td>
        {iso ? <span style={{ marginRight: 6 }}>{iso}</span> : null}
        <TextInput
          type="number"
          min="0"
          icon="pencil-alt"
          title="Editable — enter Min, Max (NTE), or a custom bill rate"
          value={viewState.billRate}
          onChange={(e) => store.setMarketRowBillRate(row, e.target.value)}
          css={{ width: 130 }}
        />
      </td>
    </tr>
  );
});

export default observer(PceRateCardDetail);
